
import React, { useState } from 'react'
import { Plus, Search, Filter, Download, FileText, Edit, Trash2, Eye, Calendar, MapPin, Users, TrendingUp, Clock, DollarSign, Target, X, History } from 'lucide-react'
import { useDiarioObra } from '../hooks/useDiarioObra'
import MetricsCard from '../components/MetricsCard'
import DiarioObraForm from '../components/DiarioObraForm'

const DiarioObra: React.FC = () => {
  const {
    diarios,
    loading,
    metrics,
    createDiario,
    updateDiario,
    deleteDiario,
    exportDiarioPDF, // 🔥 FUNÇÃO ATIVADA E CORRIGIDA
    exportMultiplePDFs, // 🔥 FUNÇÃO ATIVADA E CORRIGIDA
    getHistoricoObra,
    setObraFiltro
  } = useDiarioObra()

  const [showDiarioForm, setShowDiarioForm] = useState(false)
  const [editingDiario, setEditingDiario] = useState<any>(null)
  const [viewingDiario, setViewingDiario] = useState<any>(null)
  const [showHistorico, setShowHistorico] = useState(false)
  const [historicoObra, setHistoricoObra] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [clienteFilter, setClienteFilter] = useState<string>('all')
  const [obraFilter, setObraFilter] = useState<string>('all')
  const [dataFilter, setDataFilter] = useState<string>('')
  const [selectedDiarios, setSelectedDiarios] = useState<string[]>([])

  // Get unique clientes and obras for filters
  const clientesUnicos = Array.from(new Set(diarios.map(d => d.clienteNome)))
  const obrasUnicas = Array.from(new Set(diarios.map(d => ({ id: d.obraId, nome: d.nomeObra }))))

  // 🔥 HANDLE OBRA FILTER CHANGE - ATUALIZAR MÉTRICAS
  const handleObraFilterChange = (obraId: string) => {
    setObraFilter(obraId)
    setObraFiltro(obraId === 'all' ? '' : obraId)
  }

  // Filter diarios
  const filteredDiarios = diarios.filter(diario => {
    const matchesSearch = diario.nomeObra.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         diario.numeroContrato.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         diario.responsavelNome.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCliente = clienteFilter === 'all' || diario.clienteNome === clienteFilter
    const matchesObra = obraFilter === 'all' || diario.obraId === obraFilter
    const matchesData = !dataFilter || diario.data.startsWith(dataFilter)
    return matchesSearch && matchesCliente && matchesObra && matchesData
  })

  // Calculate percentage change
  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return ((current - previous) / previous) * 100
  }

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'rascunho': return 'bg-gray-100 text-gray-800'
      case 'finalizado': return 'bg-blue-100 text-blue-800'
      case 'aprovado': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'rascunho': return 'Rascunho'
      case 'finalizado': return 'Finalizado'
      case 'aprovado': return 'Aprovado'
      default: return status
    }
  }

  // Get clima icon
  const getClimaIcon = (clima: any) => {
    if (typeof clima === 'object' && clima.condicao) {
      switch (clima.condicao) {
        case 'sol': return '☀️'
        case 'nublado': return '☁️'
        case 'chuva': return '🌧️'
        default: return '🌤️'
      }
    }
    return '🌤️'
  }

  // Handle delete
  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este diário de obra?')) {
      await deleteDiario(id)
    }
  }

  // Handle checkbox selection
  const handleDiarioSelect = (diarioId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedDiarios(prev => [...prev, diarioId])
    } else {
      setSelectedDiarios(prev => prev.filter(id => id !== diarioId))
    }
  }

  // 🔥 HANDLE BULK PDF EXPORT - CORRIGIDO E ATIVADO
  const handleBulkPDFExport = async () => {
    const selectedDiariosData = diarios.filter(d => selectedDiarios.includes(d._id))
    
    if (selectedDiariosData.length === 0) {
      toast.error('Selecione pelo menos um diário para exportar')
      return
    }
    
    if (selectedDiariosData.length > 10) {
      toast.error('Máximo de 10 diários por exportação em massa')
      return
    }
    
    try {
      console.log(`🚀 Iniciando exportação em massa de ${selectedDiariosData.length} diários`)
      
      const exportedFiles = await exportMultiplePDFs(selectedDiariosData)
      
      if (exportedFiles && exportedFiles.length > 0) {
        console.log(`✅ Exportação em massa concluída: ${exportedFiles.length} arquivos`)
        setSelectedDiarios([]) // Clear selection after successful export
      }
      
    } catch (error) {
      console.error('❌ Erro na exportação em massa:', error)
      toast.error('Erro na exportação em massa. Tente novamente.')
    }
  }

  // 🔥 HANDLE SINGLE PDF EXPORT - CORRIGIDO E ATIVADO
  const handleSinglePDFExport = async (diario: any) => {
    // Validação robusta
    if (!diario || !diario._id) {
      console.error('❌ Diário inválido para exportação:', diario)
      toast.error('Diário inválido para exportação')
      return
    }

    try {
      console.log(`🚀 Iniciando exportação PDF individual para: ${diario.nomeObra} (ID: ${diario._id})`)
      
      // Chamar a função de exportação corrigida
      const fileName = await exportDiarioPDF(diario)
      
      if (fileName) {
        console.log(`✅ PDF individual exportado com sucesso: ${fileName}`)
      } else {
        console.warn('⚠️ Exportação retornou sem nome de arquivo')
      }
      
    } catch (error) {
      console.error('❌ Erro na exportação individual:', error)
      
      // Exibir erro específico para o usuário
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
      toast.error(`Erro ao exportar PDF: ${errorMessage}`)
    }
  }

  // 🔥 HANDLE SHOW HISTORICO
  const handleShowHistorico = async (obraId: string) => {
    try {
      const historico = await getHistoricoObra(obraId)
      setHistoricoObra(historico)
      setShowHistorico(true)
    } catch (error) {
      console.error('Erro ao carregar histórico:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando diários de obra...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Diário de Obra</h1>
              <p className="text-gray-600 mt-1">Gestão completa de diários e acompanhamento de obras</p>
            </div>
            <div className="flex items-center gap-4">
              {/* 🔥 BOTÃO DE EXPORTAÇÃO MÚLTIPLA - ATIVADO */}
              {selectedDiarios.length > 0 && (
                <button
                  onClick={handleBulkPDFExport}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors"
                  title={`Exportar ${selectedDiarios.length} PDFs selecionados`}
                >
                  <Download size={16} />
                  Exportar {selectedDiarios.length} PDFs
                </button>
              )}
              <button
                onClick={() => setShowDiarioForm(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
              >
                <Plus size={20} />
                Novo Diário
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 🔥 MÉTRICAS ATUALIZADAS POR OBRA */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricsCard
            title="Total de Diários"
            value={metrics.totalDiarios}
            icon={FileText}
            color="#3B82F6"
            description={`${calculatePercentageChange(metrics.totalDiarios, metrics.totalDiariosAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.totalDiarios, metrics.totalDiariosAnterior).toFixed(1)}% vs mês anterior${obraFilter !== 'all' ? ' (obra específica)' : ''}`}
          />
          <MetricsCard
            title="Percentual de Avanço"
            value={`${metrics.percentualAvanco.toFixed(1)}%`}
            icon={Target}
            color="#10B981"
            description={`${calculatePercentageChange(metrics.percentualAvanco, metrics.percentualAvancoAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.percentualAvanco, metrics.percentualAvancoAnterior).toFixed(1)}% vs mês anterior${obraFilter !== 'all' ? ' (obra específica)' : ''}`}
          />
          <MetricsCard
            title="Progresso Médio"
            value={`${metrics.progressoMedio.toFixed(1)}%`}
            icon={TrendingUp}
            color="#F59E0B"
            description={`${calculatePercentageChange(metrics.progressoMedio, metrics.progressoMedioAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.progressoMedio, metrics.progressoMedioAnterior).toFixed(1)}% vs mês anterior${obraFilter !== 'all' ? ' (obra específica)' : ''}`}
          />
          <MetricsCard
            title="Valor Total Medição"
            value={formatCurrency(metrics.valorTotalMedicao)}
            icon={DollarSign}
            color="#059669"
            description={`${calculatePercentageChange(metrics.valorTotalMedicao, metrics.valorTotalMedicaoAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.valorTotalMedicao, metrics.valorTotalMedicaoAnterior).toFixed(1)}% vs mês anterior${obraFilter !== 'all' ? ' (obra específica)' : ''}`}
          />
        </div>

        {/* 🔥 PREVISÃO DE FINALIZAÇÃO ATUALIZADA */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={20} className="text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Previsão de Finalização
              {obraFilter !== 'all' && obrasUnicas.find(o => o.id === obraFilter) && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  - {obrasUnicas.find(o => o.id === obraFilter)?.nome}
                </span>
              )}
            </h3>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            {formatDate(metrics.previsaoFinalizacaoObra || metrics.previsaoFinalizacao)}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Baseado no progresso médio atual de {metrics.progressoMedio.toFixed(1)}%
            {obraFilter !== 'all' && (
              <span className="text-blue-600 ml-1">(obra específica)</span>
            )}
          </p>
          {obraFilter !== 'all' && (
            <div className="mt-2 text-xs text-gray-500">
              💡 Previsão calculada com base no histórico específico desta obra
            </div>
          )}
        </div>

        {/* Filtros e Busca */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar por obra, contrato ou responsável..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter size={20} className="text-gray-400" />
                <select
                  value={obraFilter}
                  onChange={(e) => handleObraFilterChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Todas as Obras</option>
                  {obrasUnicas.map(obra => (
                    <option key={obra.id} value={obra.id}>{obra.nome}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Filter size={20} className="text-gray-400" />
                <select
                  value={clienteFilter}
                  onChange={(e) => setClienteFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Todos os Clientes</option>
                  {clientesUnicos.map(cliente => (
                    <option key={cliente} value={cliente}>{cliente}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={20} className="text-gray-400" />
                <input
                  type="month"
                  value={dataFilter}
                  onChange={(e) => setDataFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Lista de Diários */}
          <div className="overflow-x-auto">
            {filteredDiarios.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum diário encontrado</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || clienteFilter !== 'all' || obraFilter !== 'all' || dataFilter
                    ? 'Tente ajustar os filtros de busca'
                    : 'Comece criando seu primeiro diário de obra'
                  }
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDiarios(filteredDiarios.map(d => d._id))
                          } else {
                            setSelectedDiarios([])
                          }
                        }}
                        checked={selectedDiarios.length === filteredDiarios.length && filteredDiarios.length > 0}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Obra
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Centro de Custo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Responsável
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clima
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progresso
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Medição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDiarios.map((diario) => (
                    <tr key={diario._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedDiarios.includes(diario._id)}
                          onChange={(e) => handleDiarioSelect(diario._id, e.target.checked)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatDate(diario.data)}</div>
                        <div className="text-xs text-gray-500">{diario.tipo}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{diario.nomeObra}</div>
                        <div className="text-xs text-gray-500">{diario.numeroContrato}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{diario.centroCusto || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{diario.clienteNome}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{diario.responsavelNome}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-lg">{getClimaIcon(diario.clima)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${diario.progressoGeral}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {diario.progressoGeral}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(diario.valorMedicao)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(diario.status)}`}>
                          {getStatusLabel(diario.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setViewingDiario(diario)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="Visualizar"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => setEditingDiario(diario)}
                            className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50"
                            title="Editar"
                          >
                            <Edit size={16} />
                          </button>
                          {/* 🔥 BOTÃO PDF INDIVIDUAL - ATIVADO E SEM ÍCONE */}
                          <button
                            onClick={() => handleSinglePDFExport(diario)}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                            title="Exportar PDF"
                          >
                            PDF
                          </button>
                          <button
                            onClick={() => handleShowHistorico(diario.obraId)}
                            className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50"
                            title="Histórico da Obra"
                          >
                            <History size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(diario._id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <DiarioObraForm
        isOpen={showDiarioForm}
        onClose={() => setShowDiarioForm(false)}
        onSubmit={createDiario}
        title="Criar Novo Diário de Obra"
      />

      <DiarioObraForm
        isOpen={!!editingDiario}
        onClose={() => setEditingDiario(null)}
        onSubmit={(data) => updateDiario(editingDiario._id, data)}
        initialData={editingDiario}
        title="Editar Diário de Obra"
      />

      {/* Modal de Visualização */}
      {viewingDiario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">
                  Diário de Obra - {viewingDiario.nomeObra}
                </h2>
                <button
                  onClick={() => setViewingDiario(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Informações Gerais</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Data:</strong> {formatDate(viewingDiario.data)}</div>
                    <div><strong>Tipo:</strong> {viewingDiario.tipo}</div>
                    <div><strong>Centro de Custo:</strong> {viewingDiario.centroCusto || 'N/A'}</div>
                    <div><strong>Responsável:</strong> {viewingDiario.responsavelNome}</div>
                    <div><strong>Local:</strong> {viewingDiario.local}</div>
                    <div><strong>Cliente:</strong> {viewingDiario.clienteNome}</div>
                    <div><strong>Progresso:</strong> {viewingDiario.progressoGeral}%</div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Medição</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Valor da Medição:</strong> {formatCurrency(viewingDiario.valorMedicao)}</div>
                    <div><strong>Projeção Recebimento:</strong> {formatCurrency(viewingDiario.projecaoRecebimento)}</div>
                    <div><strong>Projeção Pagamento:</strong> {formatCurrency(viewingDiario.projecaoPagamento)}</div>
                  </div>
                </div>
              </div>

              {/* 🔥 VALORES DO ORÇAMENTO E PLANEJAMENTO */}
              {(viewingDiario.valoresEtapaOrcamento || (viewingDiario.valoresPlanejamento && viewingDiario.valoresPlanejamento.length > 0)) && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Valores de Referência</h3>
                  
                  {viewingDiario.valoresEtapaOrcamento && (
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <h4 className="font-medium text-blue-900 mb-2">Orçamento - {viewingDiario.valoresEtapaOrcamento.etapaNome}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-blue-700">Total:</span>
                          <div className="font-bold text-blue-900">R$ {viewingDiario.valoresEtapaOrcamento.valorTotal.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-green-700">Material:</span>
                          <div className="font-bold text-green-900">R$ {viewingDiario.valoresEtapaOrcamento.discriminacao.material.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-orange-700">Equipamento:</span>
                          <div className="font-bold text-orange-900">R$ {viewingDiario.valoresEtapaOrcamento.discriminacao.equipamento.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-purple-700">Mão de Obra:</span>
                          <div className="font-bold text-purple-900">R$ {viewingDiario.valoresEtapaOrcamento.discriminacao.maoObra.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {viewingDiario.valoresPlanejamento && viewingDiario.valoresPlanejamento.length > 0 && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-900 mb-2">Planejamento da Obra</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        {viewingDiario.valoresPlanejamento.map((etapa: any, index: number) => (
                          <div key={index}>
                            <span className="text-green-700">{etapa.etapaNome}:</span>
                            <div className="font-bold text-green-900">R$ {etapa.valorMedido.toFixed(2)}</div>
                            <div className="text-xs text-green-600">{etapa.percentualConcluido}%</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Anexos */}
              {(viewingDiario.fotos?.length > 0 || viewingDiario.documentos?.length > 0 || viewingDiario.anexos?.length > 0) && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Anexos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {viewingDiario.fotos?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Fotos ({viewingDiario.fotos.length})</h4>
                        <div className="space-y-1">
                          {viewingDiario.fotos.map((foto: any, index: number) => (
                            <div key={index} className="text-xs text-gray-600 truncate">{foto.nome}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {viewingDiario.documentos?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Documentos ({viewingDiario.documentos.length})</h4>
                        <div className="space-y-1">
                          {viewingDiario.documentos.map((doc: any, index: number) => (
                            <div key={index} className="text-xs text-gray-600 truncate">{doc.nome}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {viewingDiario.anexos?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Outros ({viewingDiario.anexos.length})</h4>
                        <div className="space-y-1">
                          {viewingDiario.anexos.map((anexo: any, index: number) => (
                            <div key={index} className="text-xs text-gray-600 truncate">{anexo.nome}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {viewingDiario.observacoes && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Observações</h3>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                    {viewingDiario.observacoes}
                  </p>
                </div>
              )}

              {/* 🔥 ATIVIDADES COM STATUS E OBSERVAÇÕES */}
              {viewingDiario.atividadesManuais && viewingDiario.atividadesManuais.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Atividades</h3>
                  <div className="space-y-3">
                    {viewingDiario.atividadesManuais.map((atividade: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-3 rounded border">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-gray-900">{atividade.descricao}</h4>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            atividade.status === 'concluida' ? 'bg-green-100 text-green-800' :
                            atividade.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' :
                            atividade.status === 'pausada' ? 'bg-yellow-100 text-yellow-800' :
                            atividade.status === 'cancelada' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {atividade.status?.replace('_', ' ').toUpperCase() || 'PLANEJADA'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <div>Responsável: {atividade.responsavel || 'N/A'}</div>
                          <div>Progresso: {atividade.percentualConcluido || 0}%</div>
                          {atividade.observacoes && (
                            <div className="mt-2 bg-white p-2 rounded border">
                              <strong>Observações:</strong> {atividade.observacoes}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewingDiario.maoObra && viewingDiario.maoObra.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Mão de Obra</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Funcionário</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Função</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Horas</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {viewingDiario.maoObra.map((mo: any, index: number) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-900">{mo.nome}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{mo.funcao}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{mo.horas}h</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(mo.valorTotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🔥 MODAL DE HISTÓRICO DA OBRA */}
      {showHistorico && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">
                  Histórico da Obra
                </h2>
                <button
                  onClick={() => setShowHistorico(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {historicoObra.length === 0 ? (
                <div className="text-center py-12">
                  <History className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum histórico encontrado</h3>
                </div>
              ) : (
                <div className="space-y-4">
                  {historicoObra.map((item, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">{item.descricao}</h4>
                          <p className="text-sm text-gray-600">Por: {item.responsavel}</p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.tipo === 'diario' ? 'bg-blue-100 text-blue-800' :
                            item.tipo === 'atividade' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.tipo.toUpperCase()}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(item.data)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DiarioObra
